import { useEffect, useRef, useState } from "preact/hooks";
import type { PaymentAmountSelection } from "../../features/checkout/amountSelection";
import type { PendingPayment } from "../../features/checkout/checkoutTypes";
import {
  createQualpayPendingPayment,
  createQualpayTokenizedCard,
  qualpayFrameStyle,
  qualpayPaymentFormId,
} from "../../features/checkout/providers/qualpayProvider";
import type {
  QpEmbeddedForm,
  QualpayCardSuccessResponse,
  QualpaySurchargeResult,
} from "../../features/checkout/providers/qualpayProvider";
import { useExternalScript, useExternalStyle } from "../../lib/externalAssets";
import { formatMoney } from "../../lib/format";
import type { QualpayPaymentSummary } from "../../features/checkout/payment";

type Props = {
  customerId: string;
  invoiceId?: string;
  payment: QualpayPaymentSummary;
  paymentAmount: PaymentAmountSelection;
  onBack: () => void;
  onPaymentReady: (payment: PendingPayment) => void;
};

declare global {
  interface Window {
    qpEmbeddedForm?: QpEmbeddedForm;
  }
}

export function QualpayPaymentPanel({
  customerId,
  invoiceId,
  payment,
  paymentAmount,
  onBack,
  onPaymentReady,
}: Props) {
  const scriptStatus = useExternalScript(payment.javascriptUrl);
  useExternalStyle(payment.cssUrl);

  return (
    <section class="payment-panel" aria-label="Payment">
      {scriptStatus === "error" ? (
        <div class="payment-provider-shell" data-provider={payment.provider}>
          <p role="alert">
            Payment options could not be loaded. Refresh the page or try again later.
          </p>
        </div>
      ) : (
        <QualpayEmbeddedCardForm
          customerId={customerId}
          invoiceId={invoiceId}
          payment={payment}
          scriptReady={scriptStatus === "ready"}
          paymentAmount={paymentAmount}
          onBack={onBack}
          onPaymentReady={onPaymentReady}
        />
      )}
    </section>
  );
}

function QualpayEmbeddedCardForm({
  customerId,
  invoiceId,
  payment,
  scriptReady,
  paymentAmount,
  onBack,
  onPaymentReady,
}: {
  customerId: string;
  invoiceId?: string;
  payment: QualpayPaymentSummary;
  scriptReady: boolean;
  paymentAmount: PaymentAmountSelection;
  onBack: () => void;
  onPaymentReady: (payment: PendingPayment) => void;
}) {
  const [nameOnCard, setNameOnCard] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const formLoaded = useRef(false);
  const billingInfo = useRef({ nameOnCard: "", zipCode: "" });
  const paymentAmountRef = useRef(paymentAmount);
  const surchargeInfo = useRef<QualpaySurchargeResult | null>(null);

  useEffect(() => {
    billingInfo.current = { nameOnCard, zipCode };
  }, [nameOnCard, zipCode]);

  useEffect(() => {
    paymentAmountRef.current = paymentAmount;
  }, [paymentAmount]);

  useEffect(() => {
    if (!scriptReady || formLoaded.current) return;

    const embeddedForm = window.qpEmbeddedForm;
    if (!embeddedForm) {
      setError("Payment options could not be loaded. Refresh the page or try again later.");
      return;
    }

    embeddedForm.loadFrame(Number(payment.merchantId), {
      formId: qualpayPaymentFormId,
      mode: payment.environment,
      transientKey: payment.embeddedKey,
      tokenize: false,
      onSuccess: handleTokenizedCard,
      onError: (response) => {
        setSubmitting(false);
        setError(response.detail?.[0] ?? "Payment information could not be validated.");
      },
      preSubmit: () => {
        setSubmitting(true);
        setError(null);
      },
      achConfig: { enabled: false },
      cardConfig: {
        enabled: true,
        checkSurchargeAllowed: (result) => {
          surchargeInfo.current = result;
        },
      },
      formFields: { cvv2: { required: true } },
      style: qualpayFrameStyle,
    });
    formLoaded.current = true;

    return () => {
      formLoaded.current = false;
      embeddedForm.unloadFrame();
    };
  }, [payment.embeddedKey, payment.merchantId, scriptReady]);

  async function handleTokenizedCard(response: QualpayCardSuccessResponse) {
    const card = createQualpayTokenizedCard(
      response,
      billingInfo.current.nameOnCard,
      billingInfo.current.zipCode,
      surchargeInfo.current,
    );
    if (!card) {
      setSubmitting(false);
      setError("Invalid card information.");
      return;
    }

    const selectedPaymentAmount = paymentAmountRef.current;
    if (selectedPaymentAmount.customAmountError) {
      setSubmitting(false);
      setError(selectedPaymentAmount.customAmountError);
      return;
    }

    try {
      onPaymentReady(
        createQualpayPendingPayment({
          card,
          customerId,
          invoiceId,
          payment,
          paymentAmount: selectedPaymentAmount,
        }),
      );
    } catch (error) {
      setError(error instanceof Error ? error.message : "Unable to submit payment.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form id={qualpayPaymentFormId} class="qualpay-form">
      <div class="payment-step-summary">
        <span>Payment amount</span>
        <strong>{formatMoney(paymentAmount.amount)}</strong>
        <button class="link-action" type="button" onClick={onBack} disabled={submitting}>
          Change
        </button>
      </div>

      <label>
        <span>Name on card</span>
        <input
          value={nameOnCard}
          onInput={(event) => setNameOnCard(event.currentTarget.value)}
          autocomplete="cc-name"
          required
        />
      </label>

      <div class="qualpay-field">
        <span>Card information</span>
        <div id="qp-embedded-container"></div>
      </div>

      <label>
        <span>Billing ZIP code</span>
        <input
          value={zipCode}
          onInput={(event) => setZipCode(event.currentTarget.value)}
          autocomplete="postal-code"
          inputMode="numeric"
          required
        />
      </label>

      {payment.surchargePercentage != null && payment.surchargePercentage > 0 && (
        <p class="payment-note">
          Credit card payments may include a {payment.surchargePercentage}% surcharge. Debit and
          prepaid cards are not surcharged.
        </p>
      )}

      {error && (
        <p class="payment-error" role="alert">
          {error}
        </p>
      )}

      <button
        class="primary-action"
        type="submit"
        disabled={submitting || !scriptReady || Boolean(paymentAmount.customAmountError)}
      >
        {submitting ? "Submitting..." : `Pay ${formatMoney(paymentAmount.amount)}`}
      </button>
    </form>
  );
}
